import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { completeMFA, fetchMeOrNull, type LoginFactorOption } from "@/lib/auth";
import { keys } from "@/lib/queries";
import { humanizeMFAError } from "@/lib/errorMessages";
import i18next from "@/lib/i18n";
import { Button, Card, Field, OtpInput, Spinner } from "@/components/ui";
import { BrandLogoMark, useBrandName } from "@/brand";
import { buildHead } from "@/lib/seo";

// Landing page the api-gateway bounces the browser back to after an OAuth
// sign-in / link. The gateway has already minted the session cookie on the
// happy path; this page just reads the outcome from the query string and
// routes the user onward (or, when the resolved account has 2FA, completes
// the challenge here using the existing /api/auth/login/mfa endpoint).
export const Route = createFileRoute("/auth/callback")({
  head: () => buildHead({ path: "/auth/callback", title: "Signing in", noindex: true }),
  validateSearch: (s: Record<string, unknown>): OAuthCallbackSearch => ({
    status: str(s.status),
    error: str(s.error),
    changed: str(s.changed),
    redirect: str(s.redirect),
    mfa: str(s.mfa),
    challenge_id: str(s.challenge_id),
    user_id: str(s.user_id),
    email: str(s.email),
    factors: str(s.factors),
  }),
  component: OAuthCallbackPage,
});

interface OAuthCallbackSearch {
  status?: string;
  error?: string;
  changed?: string;
  redirect?: string;
  mfa?: string;
  challenge_id?: string;
  user_id?: string;
  email?: string;
  factors?: string;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v !== "" ? v : undefined;
}

function OAuthCallbackPage() {
  const s = Route.useSearch();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const ran = useRef(false);

  // MFA branch renders its own form (below). Every other branch resolves
  // on mount.
  useEffect(() => {
    if (s.mfa === "1" || ran.current) return;
    ran.current = true;
    void (async () => {
      if (s.changed && !s.error) {
        // Email changed via provider re-auth — back to security settings.
        navigate({ to: "/settings/security", search: {} as never });
        return;
      }
      if (s.error) return; // render the error card below (sign-in OR email-change failure)
      // Login success: the cookie is already set, but the redirect couldn't
      // carry the CSRF token in a body. Pull /auth/me (fetchMeOrNull stashes
      // the token) and prime the cache before navigating, so the first write
      // after sign-in carries X-CSRF-Token instead of 403-ing GATEWAY_000005.
      const me = await fetchMeOrNull();
      qc.setQueryData(keys.me(), me);
      navigate({ to: (s.redirect as never) ?? "/" });
    })();
  }, [s, navigate, qc]);

  if (s.mfa === "1" && s.challenge_id) {
    return <OAuthMFAStep challengeId={s.challenge_id} factorsB64={s.factors} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="space-y-4 text-center">
          <Brand />
          {s.error ? (
            <div className="space-y-3">
              <h1 className="text-lg font-semibold text-fg">
                {s.changed ? t("callback.errorTitleChanged") : t("callback.errorTitleSignIn")}
              </h1>
              <p className="text-sm text-fg-muted">{oauthErrorMessage(s.error, s.changed)}</p>
              <Button
                block
                onClick={() =>
                  navigate(s.changed ? { to: "/settings/security", search: {} as never } : { to: "/" })
                }
              >
                {s.changed ? t("callback.backToSettings") : t("pending.backToSignIn")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <Spinner className="mx-auto text-primary" size={24} />
              <p className="text-sm text-fg-muted">{t("callback.finishing")}</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// OAuthMFAStep completes a second-factor challenge for an OAuth sign-in
// whose resolved account has 2FA. It reuses the same completeMFA endpoint
// as the password login flow.
function OAuthMFAStep({ challengeId, factorsB64 }: { challengeId: string; factorsB64?: string }) {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const factors = decodeFactors(factorsB64);
  const [factorId, setFactorId] = useState(factors[0]?.factor_id ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const activeKind = (factors.find((f) => f.factor_id === factorId)?.kind ?? "TOTP").toUpperCase();

  const submit = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await completeMFA({ challenge_id: challengeId, factor_id: factorId, code: trimmed });
      await qc.invalidateQueries({ queryKey: keys.me() });
      navigate({ to: "/" });
    } catch (e) {
      setErr(humanizeMFAError(e, activeKind));
      setCode("");
      setNonce((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="space-y-4">
          <div className="text-center">
            <Brand />
            <h1 className="mt-3 text-lg font-semibold text-fg">{t("title.twoFactor")}</h1>
            <p className="text-sm text-fg-muted">{t("callback.mfaSubtitle")}</p>
          </div>
          {factors.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2">
              {factors.map((f) => (
                <button
                  key={f.factor_id}
                  type="button"
                  onClick={() => {
                    setFactorId(f.factor_id);
                    setCode("");
                    setErr(null);
                  }}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    f.factor_id === factorId ? "border-primary text-fg" : "border-border text-fg-muted"
                  }`}
                >
                  {f.friendly_name || f.kind}
                </button>
              ))}
            </div>
          )}
          {/* The EMAIL_OTP hint names no address: the gateway's ?email= is the
              account's primary email, but the code goes to the address on the
              factor (SM-832), which can differ. */}
          <Field
            label={activeKind === "EMAIL_OTP" ? t("mfa.labels.emailOtp") : t("mfa.labels.totp")}
            htmlFor="oauth-mfa"
            hint={activeKind === "EMAIL_OTP" ? t("mfa.hints.emailOtp") : t("mfa.hints.totp")}
            error={err ?? undefined}
          >
            <OtpInput
              key={`otp-${nonce}`}
              id="oauth-mfa"
              value={code}
              onChange={setCode}
              onComplete={(v) => void submit(v)}
              invalid={!!err}
              disabled={busy}
              autoFocus
            />
          </Field>
          <Button block loading={busy} onClick={() => void submit(code)}>
            {t("mfa.verify")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Brand() {
  const brand = useBrandName();
  return (
    <div className="flex items-center justify-center gap-1.5 text-fg">
      <BrandLogoMark className="h-5 w-5 text-primary" />
      <span className="font-semibold">{brand}</span>
    </div>
  );
}

// decodeFactors reverses the gateway's base64url(JSON) packing of the
// factor options.
function decodeFactors(b64?: string): LoginFactorOption[] {
  if (!b64) return [];
  try {
    let s = b64.replace(/-/g, "+").replace(/_/g, "/");
    while (s.length % 4) s += "=";
    return JSON.parse(atob(s)) as LoginFactorOption[];
  } catch {
    return [];
  }
}

// Module-level (called during render) — resolve copy at call time via
// i18next.t so it follows the active language; the component re-renders on
// language change (useTranslation), so this always reflects the current one.
function oauthErrorMessage(code: string, provider?: string): string {
  if (code === "state") return i18next.t("auth:oauthError.state");
  if (code.startsWith("provider_")) return i18next.t("auth:oauthError.provider");
  if (code === "login_failed") return i18next.t("auth:oauthError.loginFailed");
  // OAuth email-change (Settings → Change email for a social-only account).
  const prov = oauthProviderLabel(provider);
  if (code === "change_claimed") return i18next.t("auth:oauthError.changeClaimed", { provider: prov });
  if (code === "change_taken") return i18next.t("auth:oauthError.changeTaken", { provider: prov });
  if (code === "change_no_session") return i18next.t("auth:oauthError.changeNoSession");
  if (code === "change_failed") return i18next.t("auth:oauthError.changeFailed", { provider: prov });
  return i18next.t("auth:oauthError.generic");
}

// oauthProviderLabel turns the provider token carried on ?changed= into a
// display name for error copy. Brand names stay literal; only the generic
// "social" fallback is localized.
function oauthProviderLabel(provider?: string): string {
  switch ((provider ?? "").toLowerCase()) {
    case "google":
      return "Google";
    case "github":
      return "GitHub";
    case "apple":
      return "Apple";
    default:
      return i18next.t("auth:oauthError.providerSocial");
  }
}
